import { EnumEscala } from '../../../core/settings/grupos-rentabilidade/enums/escala.enum';
import { IGruposRentabilidadeResponse } from '../../../core/settings/grupos-rentabilidade/interfaces/grupos-rentabilidade-response.interface';

/**
 * Encontra o grupo de rentabilidade pelo percentual de lucro líquido.
 *
 * Percorre os grupos em ordem decrescente de escala (Ideal → Atenção),
 * retornando o primeiro grupo cujo `percLucroLiquidoMinimo` seja menor ou
 * igual ao percentual informado. Caso nenhum grupo atenda, retorna o grupo
 * Crítico como fallback — espelhando a mesma regra de negócio do back-end Go.
 *
 * @param grupos  Lista de grupos de rentabilidade da empresa.
 * @param percLucroLiquido  Percentual de lucro líquido a ser classificado.
 * @returns O grupo de rentabilidade correspondente.
 * @throws Error quando a lista de grupos está vazia ou a margem não pode ser classificada.
 */
export function getGrupoRentabilidadeByMargem(
  grupos: IGruposRentabilidadeResponse[],
  percLucroLiquido: number
): IGruposRentabilidadeResponse {
  if (!grupos || grupos.length === 0) {
    throw new Error('Empresa não possui grupos de rentabilidade provisionados');
  }

  // Garante ordem crescente de escala independente da ordem recebida da API
  const sorted = [...grupos].sort((a, b) => a.escala - b.escala);

  let grupoCritico: IGruposRentabilidadeResponse | null = null;

  // Itera em ordem decrescente de escala (Ideal → Atenção)
  for (let i = sorted.length - 1; i >= 0; i--) {
    const grupo = sorted[i];

    // Guarda referência ao grupo Crítico para usar como fallback
    if (grupo.escala === EnumEscala.CRITICO) {
      grupoCritico = grupo;
      continue;
    }

    // Pula grupos sem mínimo definido
    if (grupo.percLucroLiquidoMinimo == null) {
      continue;
    }

    // Se a margem é maior ou igual ao mínimo do grupo, este é o grupo correto
    if (percLucroLiquido >= grupo.percLucroLiquidoMinimo) {
      return grupo;
    }
  }

  // Fallback: retorna o grupo Crítico se nenhum outro atendeu
  if (grupoCritico !== null) {
    return grupoCritico;
  }

  throw new Error('Não foi possível classificar a margem de lucro');
}
